import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute';
import RoleGuard from './auth/RoleGuard';
import { useAuth } from './auth/useAuth';
import { OFFICE_ROLES } from './types/auth';
import { AppLayout } from './components/layout';
import ActionRuntimeProvider from './components/ActionRuntimeProvider';
import { OVERSIGHT_ONLY, VERIFY_ROLES, navFor, roleLabel } from './config/nav';
import TodayPage from './pages/Today/TodayPage';
import VerifyPage from './pages/Verify/VerifyPage';
import ReleasePage from './pages/Release/ReleasePage';
import DashboardHome from './pages/DashboardHome';
import LookupPage from './pages/LookupPage';
import LodgePage from './pages/LodgePage';
import RegisterViewsPage from './pages/RegisterViewsPage';
import MyIncidentsPage from './pages/MyIncidentsPage';
import ClosureTrailPage from './pages/ClosureTrailPage';
import HelpPage from './pages/Help/HelpPage';
import RecordPage from './pages/Record/RecordPage';
import AuditsPage from './pages/Audits/AuditsPage';
import './App.css';

/**
 * Authenticated shell (13-08), in the approved mock's language.
 *
 * Routes:
 *   - `/` Today (13-09 owns `pages/Today/`);
 *   - `/verify` (13-11, widened in 13-11.1 by D-13-R-6): Oversight and every VerifierRole holder
 *     (Care Manager, Finance, HR & WHS); each lands on the rows their role verifies;
 *   - `/release` (13-12): Oversight only; anyone else is sent back to Today;
 *   - `/overview` is the old Dashboard home, kept reachable under "More", with the other
 *     existing pages (their restyle is 13-15);
 *   - `/record/:ref` (13-25): one register record, read and worked (every office role; the panels
 *     follow 13-22's gate, and practice refs `<PREFIX>-P<n>` open only in practice mode);
 *   - `/audits` (13-32): Internal Audits (schedule, complete, approve), Oversight only; anyone else is sent back
 *     to Today. An audit opens at `/record/AUD-<id>`, which the record page resolves to the audit view;
 *   - `/help` (13.1-11): the Operator's Manual page, every office role. The manual itself is
 *     the static `/manual/index.html` (not a route; excluded from the SPA fallback).
 *
 * RBAC (04-04): the whole shell is gated to the four office App Roles by the outer RoleGuard;
 * field-reporter roles are denied. App.tsx is the only file that touches routing.
 */
function QmsAdminLayout() {
  const { user, roles, logout } = useAuth();
  const { primary, pinned, more } = navFor(roles);
  return (
    <ActionRuntimeProvider>
      <AppLayout
        primary={primary}
        pinned={pinned}
        more={more}
        userDisplayName={user?.name || 'Harmony QMS'}
        roleLabel={roleLabel(roles)}
        onLogout={() => void logout()}
      >
        <Routes>
          <Route index element={<TodayPage />} />
          <Route
            path="verify"
            element={
              <RoleGuard allowedRoles={VERIFY_ROLES} redirectTo="/">
                <VerifyPage />
              </RoleGuard>
            }
          />
          <Route
            path="release"
            element={
              <RoleGuard allowedRoles={OVERSIGHT_ONLY} redirectTo="/">
                <ReleasePage />
              </RoleGuard>
            }
          />
          <Route path="overview" element={<DashboardHome />} />
          <Route path="lookup" element={<LookupPage />} />
          <Route path="lodge" element={<LodgePage />} />
          <Route path="registers" element={<RegisterViewsPage />} />
          <Route path="my-incidents" element={<MyIncidentsPage />} />
          <Route path="trail" element={<ClosureTrailPage />} />
          <Route path="record/:ref" element={<RecordPage />} />
          <Route
            path="audits"
            element={
              <RoleGuard allowedRoles={OVERSIGHT_ONLY} redirectTo="/">
                <AuditsPage />
              </RoleGuard>
            }
          />
          <Route path="help" element={<HelpPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppLayout>
    </ActionRuntimeProvider>
  );
}

/** Toasts are Sonner's one Toaster, mounted in main.tsx (the legacy ToastProvider is retired, 13-15). */
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <RoleGuard allowedRoles={OFFICE_ROLES}>
                <QmsAdminLayout />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
